import React, { ReactElement } from 'react'
import { TFunction } from 'react-i18next'
import { toast, Slide } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import { colors } from '@flowwai-frontend/design-system'
import { Loader } from '@flowwai-frontend/shared/common-components'
import {
   AlertCircleIcon,
   AlertTriangleIcon,
   ArrowNarrowRightIcon,
   CheckCircleIcon,
   IconPropsType,
   InfoCircle,
   RefreshCCW01Icon
} from '@flowwai-frontend/design-system/icons'
import { getFormattedAPIErrorDescription } from '@flowwai-frontend/shared/utils/common'

import 'react-toastify/dist/ReactToastify.css'

import {
   i18nType,
   LoadingToastAutoCloseArgs,
   LoadingToastWithPromiseArgs,
   SubmissionSuccessToastArgs,
   ToastActionType,
   ToastType,
   ToastVariant,
   ToastWithActionArgs,
   ToastWithLinkArgs,
   ToastWithRetryProps
} from '../types/ToastTypes'

import { ToastComponent } from '../components/ToastComponent/ToastComponent'
import { ToastComponentWithLink } from '../components/ToastComponent/ToastComponentWithLink'

let toastId: string | number

function dismissToast(id = toastId): void {
   if (toast.isActive(id)) {
      toast.dismiss(id)
   }
}

export const showToastWithTitleAndDescription = (
   title: string,
   toastType: ToastType,
   renderLeftIcon: (color: string) => ReactElement,
   autoClose?: number,
   description?: string
) => {
   dismissToast()
   toastId = toast(
      <ToastComponent
         title={title}
         description={description}
         toastType={toastType}
         renderLeftIcon={(color: string) => renderLeftIcon(color)}
         dismissToast={() => dismissToast()}
      />,
      {
         position: 'bottom-center',
         autoClose: autoClose,
         bodyClassName: 'font-inter',
         transition: Slide
      }
   )
}

const renderInfoLeftIcon = (color: string): React.ReactElement => (
   <InfoCircle height={20} width={20} fill={color} />
)

const renderWarningLeftIcon = (): React.ReactElement => (
   <AlertTriangleIcon height={20} width={20} />
)

const renderFailureLeftIcon = (color: string): React.ReactElement => (
   <AlertCircleIcon height={20} width={20} fill={color} />
)

const renderSuccessLeftIcon = (): React.ReactElement => (
   <CheckCircleIcon height={20} width={20} />
)

export const showCRMSuccessToast = (message: string, autoClose = 3000) => {
   showToastWithTitleAndDescription(
      message,
      'SUCCESS',
      renderSuccessLeftIcon,
      autoClose
   )
}

export const showCRMFailureToast = (error: Error | null, t: any) => {
   //TODO: need to update the type
   showToastWithTitleAndDescription(
      getFormattedAPIErrorDescription(error, t),
      'FAILURE',
      renderFailureLeftIcon
   )
}

export const showCRMFailureToastWithDescription = (
   title: string,
   description: string,
   autoClose = 3000
) => {
   //TODO: need to update the type
   showToastWithTitleAndDescription(
      title,
      'FAILURE',
      renderFailureLeftIcon,
      autoClose,
      description
   )
}

export const showCRMFailureToastWithGivenMessage = (message: string) => {
   showToastWithTitleAndDescription(message, 'FAILURE', renderFailureLeftIcon)
}

export const showCRMWarningToastWithGivenMessage = (message: string) => {
   showToastWithTitleAndDescription(message, 'WARNING', renderWarningLeftIcon)
}

export const actionCRMToast = (
   title: string,
   description: string,
   toastType: ToastType,
   toastActionProps: ToastActionType,
   renderLeftIcon?: (color: string) => React.ReactElement,
   autoClose?: number | false,
   showDismissButton?: boolean,
   uiVariant?: ToastVariant
) => {
   dismissToast()

   toastId = toast(
      <ToastComponent
         title={title}
         description={description}
         toastType={toastType}
         actions={[toastActionProps]}
         dismissToast={() => dismissToast()}
         renderLeftIcon={renderLeftIcon}
         showDismissButton={showDismissButton}
         uiVariant={uiVariant}
      />,
      {
         bodyClassName: 'font-inter text-white',
         position: 'bottom-center',
         autoClose: autoClose ?? 3000,
         transition: Slide
      }
   )
}

export const alertCRMToast = (
   title: string,
   description: string,
   toastType: ToastType,
   renderLeftIcon?: (color: string) => React.ReactElement
) => {
   dismissToast()

   toastId = toast(
      <ToastComponent
         title={title}
         description={description}
         toastType={toastType}
         actions={[]}
         dismissToast={() => dismissToast()}
         renderLeftIcon={renderLeftIcon}
      />,
      {
         bodyClassName: 'font-inter text-white',
         position: 'bottom-center',
         autoClose: 3000,
         transition: Slide
      }
   )
}

export const showAlertToastWithTitleAndDescription = (
   title: string,
   description: string
) => {
   alertCRMToast(title, description, 'FAILURE', renderFailureLeftIcon)
}

export const showToastWithRetry = (
   props: ToastWithRetryProps,
   error: Error,
   t: TFunction
): void => {
   const apiErrorMessage = getFormattedAPIErrorDescription(error, t)

   // TODO: use proper i18n strings
   const {
      title = 'Network Call Failed',
      message = apiErrorMessage,
      actionLabel = 'Retry',
      actionValue = 'RETRY',
      onClickAction = () => {
         if (props?.onClickAction) props?.onClickAction()
      },
      toastType
   } = props

   actionCRMToast(title, message, toastType, {
      label: actionLabel,
      value: actionValue,
      onClickAction: onClickAction
   })
}

export const onAPIFailureToastUtil = (error: Error, customT: i18nType) => {
   showCRMFailureToastWithDescription(
      error?.message || customT('somethingWentWrong'),
      customT('errorViewDescription')
   )
}

export const showFailureToastWithRefresh = (t: TFunction): void => {
   actionCRMToast(
      t('common:pageRefresh.title'),
      t('common:pageRefresh:description'),
      'FAILURE',
      {
         label: t('common:pageRefresh.refresh'),
         value: t('common:pageRefresh.refresh'),
         onClickAction: () => {
            window.location.reload()
         },
         hierarchy: Hierarchy.Secondary,
         subVariant: SubVariant.GrayOutline,
         renderLeftIcon: () => <RefreshCCW01Icon />
      },
      (color: string) => <AlertCircleIcon height={20} width={20} fill={color} />
   )
}

export const showFailureToastWithNavigationAction = (
   title: string,
   description: string,
   buttonTitle: string,
   onClickAction: () => void
): void => {
   actionCRMToast(
      title,
      description,
      'FAILURE',
      {
         label: buttonTitle,
         value: buttonTitle,
         onClickAction: onClickAction,
         hierarchy: Hierarchy.Link,
         subVariant: SubVariant.Gray,
         renderRightIcon: () => (
            <ArrowNarrowRightIcon
               width={20}
               height={20}
               stroke={colors['error-700']}
            />
         )
      },
      renderFailureLeftIcon
   )
}

export const showSuccessToastWithNavigationAction = (
   title: string,
   description: string,
   buttonTitle: string,
   onClickAction: () => void
): void => {
   actionCRMToast(
      title,
      description,
      'SUCCESS',
      {
         label: buttonTitle,
         value: buttonTitle,
         onClickAction: onClickAction,
         hierarchy: Hierarchy.Link,
         subVariant: SubVariant.Gray,
         renderRightIcon: () => (
            <ArrowNarrowRightIcon
               width={20}
               height={20}
               stroke={colors['success-700']}
            />
         )
      },
      renderSuccessLeftIcon
   )
}

export const showLoadingToastWithPromise = (
   args: LoadingToastWithPromiseArgs
) => {
   const { promise, successMessage, loadingMessage } = args

   dismissToast()

   toastId = toast.loading(loadingMessage, {
      position: 'top-right',
      className: '!p-4 !rounded-xl !shadow-lg',
      icon: () => <Loader width={24} height={24} />,
      transition: Slide
   })

   promise
      .then(res => {
         toast.update(toastId, {
            className: '',
            isLoading: false,
            render: (
               <ToastComponent
                  title={successMessage}
                  toastType={'SUCCESS'}
                  dismissToast={dismissToast}
                  toastClassName={'!max-w-[300px]'}
               />
            ),
            icon: undefined,
            autoClose: 2000
         })
      })
      .catch(errorMessage => {
         toast.update(toastId, {
            isLoading: false,
            className: '',
            render: (
               <ToastComponent
                  title={errorMessage.errorTitle || 'Something Went Wrong'}
                  description={errorMessage.errorDescription}
                  toastType={'FAILURE'}
                  dismissToast={dismissToast}
                  toastClassName={'!max-w-[300px]'}
               />
            ),
            autoClose: 3000,
            icon: undefined
         })
      })
}

export const showLoadingToastAutoClose = (args: LoadingToastAutoCloseArgs) => {
   const { promises, loadingMessage, successMessage, failureMessage } = args

   dismissToast()

   const loadingToastId = toast(
      <ToastComponent
         title={loadingMessage}
         toastType={'PRIMARY'}
         dismissToast={dismissToast}
         toastClassName={'!max-w-[350px] '}
         renderLeftIcon={() => (
            <InfoCircle width={20} height={20} fill={colors['primary-600']} />
         )}
      />,
      {
         position: 'bottom-center',
         autoClose: false,
         className: '',
         transition: Slide,
         isLoading: false,
         icon: undefined
      }
   )

   Promise.all(promises)
      .then(() => {
         dismissToast(loadingToastId)
         showCRMSuccessToast(successMessage)
      })
      .catch(() => {
         dismissToast(loadingToastId)
         showCRMFailureToastWithGivenMessage(failureMessage)
      })
}

export const showSubmissionSuccessToast = (
   args: SubmissionSuccessToastArgs
) => {
   const {
      title,
      toastType = 'DEFAULT',
      description = '',
      actionButtonTitle,
      onClickAction,
      icon
   } = args

   const handleOnClickAction = (): void => {
      dismissToast()
      onClickAction()
   }

   actionCRMToast(
      title,
      description,
      toastType,
      {
         label: actionButtonTitle,
         value: actionButtonTitle,
         hierarchy: Hierarchy.Primary,
         subVariant: SubVariant.Primary,
         onClickAction: handleOnClickAction
      },
      icon,
      false
   )
}

export const showToastWithLink = (args: ToastWithLinkArgs) => {
   const {
      title,
      toastType = 'DEFAULT',
      linkConfig,
      leftIcon,
      toastClassName
   } = args
   dismissToast()

   toastId = toast(
      <ToastComponentWithLink
         title={title}
         toastType={toastType}
         link={linkConfig}
         dismissToast={dismissToast}
         renderLeftIcon={leftIcon}
         toastClassName={toastClassName}
      />,
      {
         bodyClassName: 'font-inter text-white',
         position: 'bottom-center',
         autoClose: 3000,
         transition: Slide
      }
   )
}

const renderLeftIconBasedOnVariant = (
   toastType: ToastType,
   color: string
): React.ReactElement => {
   switch (toastType) {
      case 'FAILURE':
         return renderFailureLeftIcon(color)
      case 'SUCCESS':
         return renderSuccessLeftIcon()
      case 'WARNING':
         return renderWarningLeftIcon()
      case 'PRIMARY': {
         return renderInfoLeftIcon(color)
      }
      default:
         return <></>
   }
}

export const showToastWithAction = (args: ToastWithActionArgs): void => {
   const {
      title,
      buttonTitle,
      variant,
      onClickAction,
      description,
      showDismissButton = false,
      uiVariant = 'INLINE'
   } = args

   const renderLeftIcon = (color: string): React.ReactElement =>
      renderLeftIconBasedOnVariant(variant, color)

   const renderButtonRightIcon = (
      iconProps?: IconPropsType
   ): React.ReactElement => (
      <ArrowNarrowRightIcon
         width={20}
         height={20}
         stroke={iconProps?.stroke ?? colors['gray-700']}
      />
   )

   actionCRMToast(
      title,
      description ?? '',
      variant,
      {
         label: buttonTitle,
         value: buttonTitle,
         onClickAction: onClickAction,
         hierarchy: Hierarchy.Link,
         subVariant: SubVariant.Primary,
         renderRightIcon: renderButtonRightIcon,
         containerClassName: 'shrink-0 bg-[transparent] mt-[0px] mr-[0px]'
      },
      renderLeftIcon,
      undefined,
      showDismissButton,
      uiVariant
   )
}
